import { isContractAddress } from '@/domain/chain-trust'

/**
 * The contracts `GET /config` names, read off the payload — and nothing else.
 *
 * **Eager, and it has to stay viem-free.** `app/runtime.ts` reads these on cold
 * load to validate the payload, so a static import of the signing stack here
 * would put it on every anonymous visitor's first paint (invariant 13). The
 * signing half of gasless funding is `wallet/funding.ts`, which is reached
 * only through `<WalletProvider>`'s `import()`.
 *
 * **Every address comes from `GET /config`** — `chain.collateral_address`
 * (USDU), `chain.exchange_address` (the settlement contract) and, since gasless
 * funding, `chain.backing_address` (USDC) — never a constant, never derived,
 * never read out of an EIP-712 domain even where it is the same value (§1.5,
 * §F28k). A client that hardcoded one would act on the wrong contract on any
 * chain but the one it was built for, and nothing about the failure would name
 * the address.
 *
 * **There is no approval left to grant** (`backend/GASLESS-PLAN.md` decision
 * 3). Adding funds allows the settlement contract to collect a trader's losses
 * from their USDU without limit, so this module no longer reads or sends an
 * `approve` — it was `wallet/approvals.ts` until then, and its bounded,
 * up-only approval is history.
 */

export interface ChainTargets {
  /** USDU. `chain.collateral_address`. */
  collateral: `0x${string}`
  /** The settlement contract. `chain.exchange_address`. */
  exchange: `0x${string}`
  /**
   * The chain these addresses live on, for a **mismatch check** — not to pin a
   * call. Passing it to wagmi would switch chains under the user; a wallet on
   * the wrong chain is something to *detect and say*, because a transaction or
   * a signature made there looks like a success and moves nothing. The one
   * switch is `<WalletProvider>`'s `ensureVenueChain`, and it runs only from a
   * click on a button that names the network.
   */
  chainId: number
}

/** `/config.chain.relay` — gasless funding, when the venue has a relayer. */
export interface RelayLimits {
  enabled: boolean
  /** Micro-USDC. */
  minDepositMicro: bigint
  minWithdrawalMicro: bigint
  maxSignatureTtlMs: number
}

/** What "Add funds" and "Withdraw" sign against, all of it from `/config`. */
export interface FundingTargets extends ChainTargets {
  /** USDC. `chain.backing_address`. */
  backing: `0x${string}`
  /** USDC's EIP-712 domain name and version, as the token reports them. */
  backingName: string
  backingVersion: string
  /** `config.eip712.withdraw_domain`, as served — `eip712.ts` renames it. */
  withdrawDomain: unknown
  relay: RelayLimits
}

/**
 * Pull the two addresses and the chain id off `GET /config`, or `null` if the
 * venue has not published them.
 *
 * `null` rather than a guess. Every one of these is `required` on
 * `ExchangeConfig.chain`, so absence means the payload is not what this client
 * expects — and approving against an address invented to fill a gap is the one
 * outcome worth refusing outright. `conditional_tokens_address` is not read:
 * `docs/06` §F93 removed it from the wire, and a venue still sending it is
 * sending a field nothing here acts on.
 */
export function targetsFrom(config: unknown): ChainTargets | null {
  const chain = (config as { chain?: Record<string, unknown> } | null)?.chain
  const collateral = addressOf(chain?.collateral_address)
  const exchange = addressOf(chain?.exchange_address)
  const chainId = chain?.chain_id
  if (!collateral || !exchange || typeof chainId !== 'number') return null
  return { collateral, exchange, chainId }
}

/**
 * Forty hex digits and not the zero address — `domain/chain-trust.ts`'s
 * predicate, shared rather than restated.
 *
 * **Shape is the floor and not the check.** Every 40-hex string is a
 * well-formed address, including one belonging to a contract that takes the
 * approval and keeps the money: what decides whether these addresses may be
 * acted on is `verifyChainTargets`, which the send paths in `<WalletProvider>`
 * ask before every signature and transaction that moves money (§F48d). This function
 * exists so `targetsFrom` can say "the payload is not what this client
 * expects", which is a different question.
 */
function addressOf(value: unknown): `0x${string}` | null {
  return isContractAddress(value) ? value : null
}

/**
 * Everything gasless funding signs against, or `null` when the payload lacks
 * any of it — a venue from before gasless funding, or the mock. `null` means
 * "the direct path only", which every screen offers anyway.
 */
export function fundingFrom(config: unknown): FundingTargets | null {
  const targets = targetsFrom(config)
  const root = config as { chain?: Record<string, unknown>; eip712?: Record<string, unknown> } | null
  const chain = root?.chain
  const backing = addressOf(chain?.backing_address)
  const domain = chain?.backing_eip712 as { name?: unknown; version?: unknown } | undefined
  const relay = chain?.relay as Record<string, unknown> | undefined
  const withdrawDomain = root?.eip712?.withdraw_domain
  if (!targets || !backing || typeof domain?.name !== 'string' || typeof domain.version !== 'string') return null
  if (!withdrawDomain || typeof withdrawDomain !== 'object') return null
  return {
    ...targets,
    backing,
    backingName: domain.name,
    backingVersion: domain.version,
    withdrawDomain,
    relay: {
      enabled: relay?.enabled === true,
      minDepositMicro: microOf(relay?.min_deposit),
      minWithdrawalMicro: microOf(relay?.min_withdrawal),
      maxSignatureTtlMs: typeof relay?.max_signature_ttl_ms === 'number' ? relay.max_signature_ttl_ms : 0,
    },
  }
}

/** A 6dp decimal string as micro-units; anything else is zero. */
function microOf(value: unknown): bigint {
  if (typeof value !== 'string') return 0n
  const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(value)
  if (!match) return 0n
  return BigInt(match[1]!) * 1_000_000n + BigInt((match[2] ?? '').padEnd(6, '0'))
}
